import React from "react";
import { useToggleModal } from "../../hooks/useToggleModel";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Modal } from "../modal";
import { Button } from "../Button";
import { MdOutlineSimCardDownload } from "react-icons/md";
import { Input } from "../Input";
import axios from "axios";

type GenerateRestaurantReportProps = {
  restaurantId: number;
  branchId?: string;
  type: "restaurant" | "branch";
};

export const GenerateRestaurantReport: React.FC<
  GenerateRestaurantReportProps
> = ({ restaurantId, branchId, type }) => {
  const [openModel, toggleModel] = useToggleModal();

  const formik = useFormik({
    initialValues: {
      startDate: "",
      endDate: "",
    },
    validationSchema: Yup.object({
      startDate: Yup.date().nullable(),
      endDate: Yup.date().nullable(),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await axios.get(
          `${
            type === "restaurant"
              ? `/restaurant/restaurant-report?restaurantId=${restaurantId}`
              : `/restaurant/branch-report?branchId=${branchId}`
          }${values.startDate ? `&startDate=${values.startDate}` : ""}${
            values.endDate ? `&endDate=${values.endDate}` : ""
          }`,
          {
            responseType: "blob",
          }
        );

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const disposition = response.headers["content-disposition"];
        const match = disposition?.match(/filename="?(.+)"?/);
        const filename = match?.[1] || "report.pdf";

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading report", error);
      } finally {
        resetForm();
        toggleModel();
      }
    },
  });

  return (
    <>
      <Button
        lightGreen
        className="w-fit flex items-center gap-2 focus:outline-none focus:ring-0"
        onClick={toggleModel}
      >
        <MdOutlineSimCardDownload className="text-lg" />
        Report
      </Button>

      <Modal
        title={
          type === "restaurant"
            ? "Generate Restaurant Report"
            : "Generate Branch Report"
        }
        handleClose={toggleModel}
        isOpen={openModel}
      >
        <form
          onSubmit={formik.handleSubmit}
          className="w-full flex flex-col gap-4 transition-all"
        >
          <div>
            <label className="text-sm text-green-500 font-semibold">
              Start Date
            </label>
            <Input
              id="start-date"
              type="date"
              className="w-full h-10 border-2 border-green-500 px-3 text-gray-600 font-medium cursor-pointer"
              onClick={(e) => {
                const input = e.currentTarget as HTMLInputElement & {
                  showPicker?: () => void;
                };
                input.showPicker?.();
              }}
              {...formik.getFieldProps("startDate")}
            />
          </div>

          <div>
            <label className="text-sm text-green-500 font-semibold">
              End Date
            </label>
            <Input
              id="end-date"
              type="date"
              className="w-full h-10 border-2 border-green-500 px-3 text-gray-600 font-medium cursor-pointer"
              onClick={(e) => {
                const input = e.currentTarget as HTMLInputElement & {
                  showPicker?: () => void;
                };
                input.showPicker?.();
              }}
              {...formik.getFieldProps("endDate")}
            />
          </div>

          <Button
            className="w-full rounded-lg text-md py-2 mt-4 !bg-primary !text-white !font-bold focus:outline-none focus:ring-0 hover:!bg-primary/80"
            type="submit"
            disabled={formik.isSubmitting}
            loading={formik.isSubmitting}
          >
            Generate
          </Button>
        </form>
      </Modal>
    </>
  );
};
